import {
  FileImageOutlined,
  LinkOutlined,
  SaveOutlined,
} from "@ant-design/icons";
import {
  Form,
  Input,
  InputNumber,
  Modal,
  Select,
  Typography,
  message,
} from "antd";
import { useEffect, useState } from "react";
import CustomAttributesEditor from "../CustomAttributesEditor";
import OutputFormatPicker from "../OutputFormatPicker";
import { sizeRules } from "../specDefaults";
const { Text, Title } = Typography;

export default function SpecFormModal({ open, spec, onCancel, onSave }) {
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (!open) return;
    form.resetFields();
    form.setFieldsValue(
      spec || { unit: "px", dpi: 300, customAttributes: {}, sortOrder: 0 },
    );
  }, [open, spec, form]);
  const submit = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);
      await onSave(values);
    } catch (error) {
      if (!error.errorFields)
        message.error(error.response?.data?.message || error.message);
    } finally {
      setSaving(false);
    }
  };
  return (
    <Modal
      className="specstudio-dialog-modal specstudio-spec-modal"
      open={open}
      onCancel={onCancel}
      onOk={submit}
      okText={spec ? "Lưu thay đổi" : "Thêm vùng in"}
      cancelText="Hủy"
      okButtonProps={{ icon: <SaveOutlined />, loading: saving }}
      width={780}
      destroyOnClose
      title={null}
    >
      <div className="specstudio-spec-heading">
        <span className="specstudio-spec-icon">
          <FileImageOutlined />
        </span>
        <div>
          <Text className="specstudio-eyebrow">
            SPECSTUDIO · PRINT REQUIREMENTS
          </Text>
          <Title level={2}>
            {spec ? "Chỉnh sửa thông số" : "Thêm vùng in"}
          </Title>
          <Text type="secondary">
            Khai báo kích thước file, định dạng xuất và template cho vùng in
            này.
          </Text>
        </div>
      </div>
      <Form form={form} layout="vertical" requiredMark>
        <section className="specstudio-spec-section">
          <div className="specstudio-spec-section-title">
            <span className="specstudio-section-index">01</span>
            <div>
              <Title level={5}>Thông số vùng in</Title>
              <Text type="secondary">Tên vùng và kích thước thiết kế.</Text>
            </div>
          </div>
          <Form.Item
            name="areaName"
            label="Tên vùng in"
            rules={[
              { required: true, whitespace: true, message: "Nhập tên vùng in" },
              { max: 100, message: "Tối đa 100 ký tự" },
            ]}
          >
            <Input size="large" placeholder="VD: Full Front, Left Chest" />
          </Form.Item>
          <div className="specstudio-spec-dimensions">
            <Form.Item name="width" label="Chiều rộng" rules={sizeRules}>
              <InputNumber min={0} placeholder="4500" />
            </Form.Item>
            <span className="specstudio-dimension-x">×</span>
            <Form.Item name="height" label="Chiều cao" rules={sizeRules}>
              <InputNumber min={0} placeholder="5400" />
            </Form.Item>
            <Form.Item name="unit" label="Đơn vị" rules={[{ required: true }]}>
              <Select
                size="large"
                options={[
                  { value: "px", label: "Pixels (px)" },
                  { value: "cm", label: "Centimeters (cm)" },
                  { value: "inch", label: "Inches (in)" },
                ]}
              />
            </Form.Item>
            <Form.Item name="dpi" label="Độ phân giải">
              <InputNumber min={1} placeholder="300" />
            </Form.Item>
          </div>
        </section>
        <section className="specstudio-spec-section">
          <div className="specstudio-spec-section-title">
            <span className="specstudio-section-index">02</span>
            <div>
              <Title level={5}>Định dạng &amp; template</Title>
              <Text type="secondary">
                Yêu cầu file thiết kế khi gửi nhà in.
              </Text>
            </div>
          </div>
          <div className="specstudio-spec-two-col specstudio-format-row">
            <Form.Item name="outputFormat" label="Định dạng output">
              <OutputFormatPicker />
            </Form.Item>
            <Form.Item name="backgroundType" label="Yêu cầu nền">
              <Select
                allowClear
                size="large"
                placeholder="Chọn nền"
                options={[
                  { value: "Transparent", label: "Transparent - Trong suốt" },
                  { value: "Solid Color", label: "Solid color" },
                  { value: "White", label: "White - Trắng" },
                  { value: "Custom", label: "Custom" },
                ]}
              />
            </Form.Item>
          </div>
          <Form.Item name="templateFileUrl" label="Template resource">
            <Input
              size="large"
              prefix={<LinkOutlined />}
              placeholder="Dán direct URL / Google Drive / S3 CDN"
            />
          </Form.Item>
          <Text className="specstudio-template-help">
            Dùng URL có thể truy cập trực tiếp để designer tải template.
          </Text>
        </section>
        <section className="specstudio-spec-section">
          <div className="specstudio-spec-section-title">
            <span className="specstudio-section-index">03</span>
            <div>
              <Title level={5}>Thông số tùy chỉnh</Title>
              <Text type="secondary">
                Thêm thông tin riêng như số mũi thêu hoặc số màu tối đa.
              </Text>
            </div>
          </div>
          <Form.Item name="customAttributes" noStyle>
            <CustomAttributesEditor />
          </Form.Item>
        </section>
        <Form.Item name="sortOrder" hidden>
          <InputNumber />
        </Form.Item>
      </Form>
      <div className="specstudio-spec-modal-foot">
        <Text type="secondary">
          Thông số này chỉ áp dụng cho vùng in hiện tại.
        </Text>
      </div>
    </Modal>
  );
}
